import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, getFullImageUrl, getProductById } from "../api";
import { getFallbackImage } from "../utils/imageLoader";
import './OrderHistory.css';
import { FaChevronRight, FaBox, FaClock, FaCheckCircle, FaTimesCircle, FaTruck, FaShoppingBag, FaTimes } from "react-icons/fa";
import ProductModal from "../components/ProductModal";
import { Spinner, ProgressBar } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { addToCart } from "../redux/cartSlice";
import { toast } from "react-hot-toast";

const OrderHistoryPage = () => {
    // Initializations
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [sortBy, setSortBy] = useState("newest");
    const [page, setPage] = useState(1);
    const pageSize = 10;
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [reorderingId, setReorderingId] = useState(null);

    // Check logged in or not based on local storage
    useEffect(() => {
        const fetchOrders = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }
            setLoading(true);

            try {
                // Fetch all orders
                const data = await apiFetch('/api/orders/all');
                const list = data.orders || data.data || [];
                // Sort newest first
                list.sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt));
                // Filter orders based on payment and status
                const isOrderVisible = (order) => {
                    const paymentStatus = order.payment && order.payment.status ? String(order.payment.status).toLowerCase() : '';
                    const orderStatus = order.status ? String(order.status).toLowerCase() : (order.orderStatus ? String(order.orderStatus).toLowerCase() : '');

                    // Determine if order should be visible based on payment and status
                    if (paymentStatus === 'success' || paymentStatus === 'paid') return true;
                    const visibleStatuses = ['confirmed', 'processing', 'shipped', 'delivered', 'completed'];
                    if (visibleStatuses.includes(orderStatus)) return true;

                    return false;
                };
                // filtered list
                const filtered = list.filter(isOrderVisible);
                setOrders(filtered);
            } catch (err) {
                setError(err.message || 'Failed to fetch orders');
            } finally {
                setLoading(false);
            }
        };
        fetchOrders();
    }, [navigate]);

    // Get image for order
    const getImageForOrder = (order) => {
        // get image from the first order item
        const firstItems = order.orderItems || [];
        if (firstItems.length === 0) return getFallbackImage();
        const first = firstItems[0];
        const prod = first.product || {};
        const candidates = [prod?.images?.[0]?.url, prod?.images?.[0], prod?.imageUrl, prod?.image, first?.imageUrl, first?.image];
        const found = candidates.find(c => !!c);
        return getFullImageUrl(found) || getFallbackImage();
    };

    // Format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };
    // Reorder handler
    const handleReorder = async (order) => {
        setReorderingId(order._id);
        try {
            const items = order.orderItems || [];
            if (items.length === 0) {
                toast.error('No items to reorder');
                return;
            }
            // Reorder items
            let successCount = 0;
            let failCount = 0;
            // Iterate through order items and add to cart
            for (const item of items) {
                try {
                    const productId = item.product?._id || item.product;
                    if (!productId) {
                        failCount++;
                        continue;
                    }
                    // Fetch full product details
                    const fullProduct = await getProductById(productId);
                    const product = fullProduct.data || fullProduct;
                    
                    if (!product) {
                        failCount++;
                        continue;
                    }
                    // cart item
                    const cartItem = {
                        id: product._id,
                        title: product.name || product.title,
                        price: product.price || item.price,
                        // Use selected options if available
                        image: getFullImageUrl(product.images?.[0]?.url || product.images?.[0] || product.image) || getFallbackImage(),
                        quantity: item.quantity || 1
                    };
                    // Dispatch add to cart action
                    dispatch(addToCart(cartItem));
                    successCount++;
                } catch (err) {
                    console.error('Failed to add item:', err);
                    failCount++;
                }
            }
            // Show notifications
            if (successCount > 0) {
                window.dispatchEvent(new CustomEvent('cartNotification', {
                    detail: { 
                        type: 'cart', 
                        subType: 'add', 
                        message: `${successCount} item${successCount > 1 ? 's' : ''} added to cart`,
                        variantCount: successCount
                    }
                }));
            }
            // Show failure toast
            if (failCount > 0) {
                toast.error(`${failCount} item${failCount > 1 ? 's' : ''} could not be added`);
            }
        } catch (err) {
            toast.error('Failed to reorder: ' + err.message);
        } finally {
            setReorderingId(null);
        }
    };

    // Get status icon and color
    const getStatusInfo = (status) => {
        const statusStr = String(status).toLowerCase();

        // Determine icon and color based on status
        switch(statusStr) {
            case 'pending':
            case 'processing':
                return {
                    icon: <FaClock />,
                    color: '#f39c12',
                    bgColor: '#fef5e7'
                };
            case 'confirmed':
                return {
                    icon: <FaShoppingBag />,
                    color: '#3498db',
                    bgColor: '#ebf5fb'
                };
            case 'shipped':
                return {
                    icon: <FaTruck />,
                    color: '#9b59b6',
                    bgColor: '#f4ecf7'
                };
            case 'delivered':
            case 'completed':
                return {
                    icon: <FaCheckCircle />,
                    color: '#27ae60',
                    bgColor: '#eafaf1'
                };
            case 'cancelled':
            case 'refunded':
                return {
                    icon: <FaTimesCircle />,
                    color: '#e74c3c',
                    bgColor: '#fdedec'
                };
            default:
                return {
                    icon: <FaBox />,
                    color: '#7f8c8d',
                    bgColor: '#f2f3f4'
                };
        }
    };

    // derived filtered/sorted/paginated list
    const normalized = (s) => String(s || '').toLowerCase();
    const matchesQuery = (order) => {
        // Check if order matches the search query
        if (!query) return true;
        // concatenate orderId and first product name
        const txt = `${order.orderId || ''} ${order._id || ''} ${(order.orderItems?.[0]?.product?.name) || ''}`.toLowerCase();
        return txt.includes(query.toLowerCase());
    };
    // status filter
    const matchesStatus = (order) => {
        // Check if order matches the status filter
        if (statusFilter === 'all') return true;
        const ordStatus = normalized(order.status || order.orderStatus);
        const payStatus = normalized(order.payment?.status);
        return ordStatus === statusFilter || payStatus === statusFilter;
    };
    // apply filtering, sorting, and pagination
    const sorted = [...orders]
        .filter(matchesQuery)
        .filter(matchesStatus)
        .sort((a,b) => {
            // Sort based on selected criteria
            if (sortBy === 'newest') return new Date(b.createdAt) - new Date(a.createdAt);
            if (sortBy === 'oldest') return new Date(a.createdAt) - new Date(b.createdAt);
            const at = (a.total_amount || 0);
            const bt = (b.total_amount || 0);
            // Amount sorting
            return sortBy === 'amount_desc' ? bt - at : at - bt;
        });
        // Pagination calculations
    const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
    const currentPage = Math.min(page, totalPages);
    const paged = sorted.slice((currentPage-1)*pageSize, currentPage*pageSize);

    return (
        <div className="order-history-container min-vh-100 bg-white">
            <div className="container py-4 py-md-5">
                {/* Header */}
                <div className="mb-4 mb-md-5">
                    <div className="d-flex align-items-center mb-3">
                        <div className="p-3 rounded-circle bg-purple-10 me-3">
                            <FaBox className="text-purple" size={24} />
                        </div>
                        <div>
                            <h1 className="fw-bold text-dark mb-1">Order History</h1>
                            <p className="text-muted mb-0">Track and manage all your orders</p>
                        </div>
                    </div>
                    {/* Controls */}
                    <div className="row g-3">
                        <div className="col-md-6">
                            <div className="position-relative">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by Order ID or Product"
                                    value={query}
                                    onChange={(e)=>{setPage(1); setQuery(e.target.value);}}
                                />
                                {query && (
                                    <button
                                        type="button"
                                        className="btn position-absolute top-50 end-0 translate-middle-y me-2"
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#6c757d',
                                            fontSize: '14px',
                                            padding: '0',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => {setPage(1); setQuery('');}}
                                        aria-label="Clear search"
                                    >
                                        <FaTimes />
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="col-md-3">
                            <select className="form-select" value={statusFilter} onChange={(e)=>{setPage(1); setStatusFilter(e.target.value);}}>
                                <option value="all">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="confirmed">Confirmed</option>
                                <option value="processing">Processing</option>
                                <option value="shipped">Shipped</option>
                                <option value="delivered">Delivered</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="refunded">Refunded</option>
                                <option value="paid">Paid</option>
                                <option value="success">Success</option>
                            </select>
                        </div>
                        <div className="col-md-3">
                            <select className="form-select" value={sortBy} onChange={(e)=>setSortBy(e.target.value)}>
                                <option value="newest">Newest First</option>
                                <option value="oldest">Oldest First</option>
                                <option value="amount_desc">Amount: High to Low</option>
                                <option value="amount_asc">Amount: Low to High</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Error Alert */}
                {error && (
                    <div className="alert alert-danger border-0 shadow-sm mb-4">
                        <div className="d-flex align-items-center">
                            <FaTimesCircle className="me-2" />
                            <span>{error}</span>
                        </div>
                    </div>
                )}

                {/* Loading State */}
                {loading && (
                    <div className="text-center py-5 my-5">
                        <div className="text-center">
                            <Spinner animation="border" className="loading-spinner" style={{ color: "#82287A", width: '3rem', height: '3rem' }} />
                            <div className="mt-3">
                                <p className="text-purple mb-2">Loading your orders...</p>
                                <ProgressBar 
                                    now={100} 
                                    animated 
                                    style={{ height: '4px', width: '200px', margin: '0 auto' }}
                                    variant="purple"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Empty State */}
                {!loading && orders.length === 0 && (
                    <div className="text-center py-5 my-5">
                        <div className="empty-state-icon mb-4">
                            <div className="empty-state-circle">
                                <FaBox size={48} className="text-purple" />
                            </div>
                        </div>
                        <h3 className="fw-semibold text-dark mb-3">No Orders Yet</h3>
                        <p className="text-muted mb-4">You haven't placed any orders yet.</p>
                        <Link to="/products" className="btn btn-purple btn-lg px-4 py-3">
                            Start Shopping
                        </Link>
                    </div>
                )}

                {/* Orders List */}
                {!loading && sorted.length > 0 && (
                    <div className="row g-4">
                        {paged.map((order) => {
                            const statusInfo = getStatusInfo(order.status || order.orderStatus);
                            const orderItems = order.orderItems || [];
                            const productName = orderItems[0]?.product?.name || orderItems[0]?.product?.title || "Product";

                            return (
                                <div key={order._id} className="col-12">
                                    <div className="order-card card border-0 shadow-sm hover-lift">
                                        <div className="card-body p-4">
                                            <div className="row align-items-center">
                                                {/* Order Image */}
                                                <div className="col-md-2 col-3">
                                                    <div
                                                        className="order-image-container position-relative rounded overflow-hidden"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={async () => {
                                                            const firstItem = orderItems[0];
                                                            if (firstItem && firstItem.product) {
                                                                try {
                                                                    const fullProduct = await getProductById(firstItem.product._id || firstItem.product);
                                                                    setSelectedProduct(fullProduct.data);
                                                                    setModalOpen(true);
                                                                } catch (err) {
                                                                    console.error('Failed to load product:', err);
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <img
                                                            src={getImageForOrder(order)}
                                                            alt={productName}
                                                            className="order-image w-100"
                                                            onError={(e) => {
                                                                e.target.src = getFallbackImage();
                                                            }}
                                                        />
                                                        <div className="order-image-overlay"></div>
                                                    </div>
                                                </div>

                                                {/* Order Info */}
                                                <div className="col-md-5 col-9">
                                                    <div className="mb-2">
                                                        <h5 className="fw-bold text-dark mb-1 text-truncate" style={{ cursor: 'pointer' }} onClick={async () => {
                                                            const firstItem = orderItems[0];
                                                            if (firstItem && firstItem.product) {
                                                                try {
                                                                    const fullProduct = await getProductById(firstItem.product._id || firstItem.product);
                                                                    setSelectedProduct(fullProduct.data);
                                                                    setModalOpen(true);
                                                                } catch (err) {
                                                                    console.error('Failed to load product:', err);
                                                                }
                                                            }
                                                        }}>
                                                            {productName}
                                                        </h5>
                                                        <p className="text-muted small mb-2">
                                                            Order #{order.orderId || order._id?.slice(-8)}
                                                        </p>
                                                        {orderItems[0] && (orderItems[0].color || orderItems[0].selectedColor || orderItems[0].size || orderItems[0].selectedSize) && (
                                                            <div className="d-flex gap-2 mb-2">
                                                                {(orderItems[0].color || orderItems[0].selectedColor) && (
                                                                    <span style={{
                                                                        backgroundColor: '#f0e6f5',
                                                                        color: '#82278A',
                                                                        padding: '2px 8px',
                                                                        borderRadius: '4px',
                                                                        fontSize: '0.75rem',
                                                                        fontWeight: '500'
                                                                    }}>
                                                                        {orderItems[0].color || orderItems[0].selectedColor}
                                                                    </span>
                                                                )}
                                                                {(orderItems[0].size || orderItems[0].selectedSize) && (
                                                                    <span style={{
                                                                        backgroundColor: '#f0e6f5',
                                                                        color: '#82278A',
                                                                        padding: '2px 8px',
                                                                        borderRadius: '4px',
                                                                        fontSize: '0.75rem',
                                                                        fontWeight: '500'
                                                                    }}>
                                                                        Size: {orderItems[0].size || orderItems[0].selectedSize}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                                        <div className="d-flex align-items-center">
                                                            <span className="text-muted me-1">
                                                                {orderItems.length} item{orderItems.length !== 1 ? 's' : ''}
                                                            </span>
                                                        </div>
                                                        <div className="d-flex align-items-center">
                                                            <span className="text-muted me-1">•</span>
                                                            <span className="text-muted">
                                                                {formatDate(order.createdAt)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex align-items-center">
                                                        <span
                                                            className="status-badge d-inline-flex align-items-center px-3 py-1 rounded-pill"
                                                            style={{
                                                                backgroundColor: statusInfo.bgColor,
                                                                color: statusInfo.color,
                                                                border: `1px solid ${statusInfo.color}20`
                                                            }}
                                                        >
                                                            <span className="me-2">{statusInfo.icon}</span>
                                                            <span className="fw-semibold">
                                                                {String(order.status || order.orderStatus || 'Pending').toUpperCase()}
                                                            </span>
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Order Amount */}
                                                <div className="col-md-2 col-6 mt-md-0 mt-3">
                                                    <div className="text-md-end">
                                                        <div className="text-muted small mb-1">Total Amount</div>
                                                        <h4 className="fw-bold text-purple mb-0">
                                                            ₹{order.total_amount ? order.total_amount.toFixed(2) : '0.00'}
                                                        </h4>
                                                    </div>
                                                </div>

                                                {/* Actions */}
                                                <div className="col-md-3 col-6 mt-md-0 mt-3">
                                                    <div className="text-end">
                                                        <div className="d-flex gap-2 justify-content-end">
                                                            <Link
                                                                to={`/ordertracking/${order._id}`}
                                                                className="btn btn-outline-purple btn-sm d-inline-flex align-items-center px-3 py-2"
                                                            >
                                                                <span className="me-2">Track</span>
                                                                <FaChevronRight size={14} />
                                                            </Link>
                                                            <button
                                                                className="btn btn-purple btn-sm px-3"
                                                                onClick={() => handleReorder(order)}
                                                                disabled={reorderingId === order._id}
                                                            >
                                                                {reorderingId === order._id ? 'Adding...' : 'Reorder'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* No Filtered Results */}
                {!loading && orders.length > 0 && sorted.length === 0 && (
                    <div className="text-center py-5 my-5">
                        <h3 className="text-danger">No Items Found</h3>
                        <p className="text-muted">No orders match your current filters.</p>
                    </div>
                )}

                {/* Orders Count */}
                {!loading && sorted.length > 0 && (
                    <div className="mt-4 pt-2">
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="text-muted">
                                Showing {paged.length} of {sorted.length} orders
                            </span>
                            <div className="d-flex align-items-center gap-2">
                                <button className="btn btn-outline-purple btn-sm" disabled={currentPage===1} onClick={()=>setPage(p=>Math.max(1,p-1))}>Prev</button>
                                <span className="text-muted">Page {currentPage} / {totalPages}</span>
                                <button className="btn btn-outline-purple btn-sm" disabled={currentPage===totalPages} onClick={()=>setPage(p=>Math.min(totalPages,p+1))}>Next</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Product Modal */}
            {selectedProduct && (
                <ProductModal
                    post={selectedProduct}
                    isOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                />
            )}
        </div>
    );
};

export default OrderHistoryPage;